import { ErrorHandler, forwardRef, Injector, NgModule, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule, EVENT_MANAGER_PLUGINS } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { APP_BASE_HREF, CommonModule, PlatformLocation } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS } from '@angular/common/http';

import { providePrimeNG } from 'primeng/config';
import primeNgAura from '@primeng/themes/aura';
import { MessageService } from 'primeng/api';
import { QRCodeComponent } from 'angularx-qrcode';

import { BootstrapComponent } from './components/bootstrap/bootstrap.component';
import { ConfigService } from '../Core/Config/services/config.service';
import { BootstrapRoutingModule } from './bootstrap-routing.module';
import { ServiceApi } from '../api/genericApi/service-api';
import { RateLimitInterceptor } from './interceptors/rate-limit.interceptor';
import { SessionManagementInterceptor } from './interceptors/session-management.interceptor';
import { SessionManagementService } from './services/session-management.service';
import { LoginService } from './services/login.service';
import { McpOauthService } from './services/mcp-oauth.service';
import { SessionTimeoutComponent } from './components/sessionTimeout/session-timeout.component';
import { CoreModule } from '../Core/core.module';
import { LayoutModule } from '../Common/Layout/layout.module';
import { NewsModule } from '../News/news.module';
import { MonitorModule } from '../Monitor/monitor.module';
import { ReportsModule } from '../Reports/reports.module';
import { AmplifyModule } from '../Amplify/amplify.module';
import { AdminModule } from '../Admin/admin.module';
import { DiscoverModule } from '../Discover/discover.module';
import { VisibilityIntelligenceModule } from '../VisibilityIntelligence/visibility-intelligence.module';
import { MiscModule } from '../Common/Misc/misc.module';
import { TooltipModule, TooltipStyle } from 'primeng/tooltip';
import { NeptuneApi } from '../api/genericApi/neptune.api';
import { CommonDialogsModule } from '../Common/Dialogs/common-dialogs.module';

import { PasswordResetComponent } from './components/PasswordReset/password-reset.component';

import { PasswordResetService } from './components/PasswordReset/password-reset.service';
import { AccountModule } from '../Account/account.module';
import { LoginComponent } from './components/Login/login.component';
import { LoginRouteGuard } from './components/Login/login.route.guard';
import { LogoutRouteGuard } from './components/Login/logout.route.guard';
import { LoginApi } from '../api/login/login.api';
import { ErrorHandlerService } from './services/error-handler.service';
import { ForgotPasswordComponent } from './components/ForgotPassword/forgot-password.component';
import { DistributionPlanComponent } from './components/DistributionPlans/distribution-plan.component';
import { CoreDialogsModule } from '../Core/Dialogs/dialogs.module';
import { CommonFormsModule } from '../Common/Forms/forms.module';
import { CommonBasicModule } from '../Common/Basic/basic.module';
import { CommonMonitorSharedModule } from '../Common/MonitorShared/common-monitor-shared.module';
import { LocalizationModule } from '../Core/Localization/localization.module';
import { WebsocketAwareNeptuneService } from './services/websocket-aware-neptune.service';
import { ClickEventHandlerService } from './services/click-event-handler.service';
import { TermsAndConditionsComponent } from './components/TermsAndConditions/terms-and-conditions.component';
import { SocialModule } from '../Social/social.module';
import { AutomationAgentsModule } from '../AutomationAgents/automation-agents.module';
import { GlobalMessageService } from './services/global-message.service';
import { gtagFactory } from '../Common/Misc/factories/gtag.factory';
import { GtagService } from '../Common/Misc/services/gtag.service';
import { environment } from '../../environments/environment';
import { TermsAndConditionsRouteGuard } from './components/TermsAndConditions/terms-and-conditions.guard';
import { SsoLoginComponent } from './components/SsoLogin/sso-login.component';
import { PendoService } from '../Common/Misc/services/pendo.service';
import { BootstrapCoreDialogMap } from './models/core-dialog-map.model';
import { LandingPageModule } from 'app/LandingPage/landingpage.module';

@NgModule({
    imports: [
        LayoutModule,
        BootstrapRoutingModule,
        BrowserAnimationsModule,
        BrowserModule,
        NewsModule,
        MonitorModule,
        ReportsModule,
        AmplifyModule,
        CoreModule,
        AdminModule,
        DiscoverModule,
        VisibilityIntelligenceModule,
        SocialModule,
        AutomationAgentsModule,
        MiscModule,
        TooltipModule,
        CommonModule,
        CommonDialogsModule,
        CommonMonitorSharedModule,
        FormsModule,
        ReactiveFormsModule,
        CommonBasicModule,
        LocalizationModule,
        CommonFormsModule,
        AccountModule,
        CoreDialogsModule,
        QRCodeComponent,
        LandingPageModule,
    ],
    declarations: [
        BootstrapComponent,
        SessionTimeoutComponent,
        PasswordResetComponent,
        ForgotPasswordComponent,
        TermsAndConditionsComponent,
        LoginComponent,
        SsoLoginComponent,
        DistributionPlanComponent,
    ],
    exports: [
        BootstrapComponent,
    ],
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        ServiceApi,
        WebsocketAwareNeptuneService,
        {
            provide: NeptuneApi,
            useExisting: WebsocketAwareNeptuneService
        },
        LoginApi,
        SessionManagementService,
        {
            provide: APP_BASE_HREF,
            useFactory: (s: PlatformLocation) => {
                // Make sure it always ends with a `/`
                const base = s.getBaseHrefFromDOM();
                if (base.length < 1) {
                    return '/';
                } else if (base[base.length - 1] !== '/') {
                    return base + '/';
                }
                return base;
            },
            deps: [PlatformLocation]
        },
        {
            provide: 'DEPLOY_URL',
            useValue: environment.deployUrl,
            deps: [PlatformLocation]
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RateLimitInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SessionManagementInterceptor,
            deps: [ Injector, SessionManagementService ],
            multi: true
        },
        {
            provide: 'gtag',
            useFactory: gtagFactory
        },
        GtagService,
        MessageService,
        PasswordResetService,
        LoginRouteGuard,
        TermsAndConditionsRouteGuard,
        LogoutRouteGuard,
        ErrorHandlerService,
        {
            provide: ErrorHandler,
            useExisting: ErrorHandlerService
        },
        {
            provide: EVENT_MANAGER_PLUGINS,
            useExisting: ClickEventHandlerService,
            multi: true
        },
        {
            provide: 'CoreDialogMap',
            useValue: BootstrapCoreDialogMap
        },
        GlobalMessageService,
        PendoService,
        LoginService,
        McpOauthService,
        TooltipStyle,
        providePrimeNG({
            theme: {
                preset: primeNgAura,
                options: {
                    cssLayer: false,
                    darkModeSelector: false
                }
            }
        })
    ],
    bootstrap: [
        BootstrapComponent
    ]
})
export class BootstrapModule { }
